import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { FiCheckCircle, FiShield, FiLock, FiZap } from 'react-icons/fi';

export default function Audit() {
  return (
    <div className="bg-white min-h-screen">
      <SEO title="Request AI Governance Audit | Peridot" description="Get a definitive inventory of every AI tool running in your environment in 48 hours." />
      
      <div className="max-w-6xl mx-auto px-4 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        
        {/* Left Column - Value Prop */}
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold mb-6">
            <FiShield className="text-peridot-600" /> Executive Service
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight mb-6">
            Discover Your True AI Attack Surface.
          </h1>
          <p className="text-xl text-slate-600 mb-8 leading-relaxed">
            Based on our 2026 benchmark data, 86% of enterprises have critical blindspots in their AI inventory. We map your entire shadow AI footprint in 48 hours without agents.
          </p>

          <div className="space-y-6">
            {[
              { icon: <FiZap className="text-amber-500" />, title: "Instant Discovery", desc: "Identify all unauthorized LLMs, SaaS AI features, and browser extensions." },
              { icon: <FiLock className="text-blue-500" />, title: "Data Exposure Check", desc: "See exactly which departments are feeding PII into public models." },
              { icon: <FiCheckCircle className="text-peridot-500" />, title: "Board-Ready Reporting", desc: "Receive an executive dashboard quantifying your exact risk posture." }
            ].map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.1 }} className="flex gap-4">
                <div className="shrink-0 mt-1 bg-slate-50 p-2 rounded-lg border border-slate-100">
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">{item.title}</h3>
                  <p className="text-slate-600 text-sm mt-1">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right Column - Form */}
        <div className="bg-slate-900 rounded-2xl p-8 md:p-10 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-peridot-500 rounded-full blur-3xl opacity-10 translate-x-1/2 -translate-y-1/2"></div>
          
          <h2 className="text-2xl font-bold text-white mb-2">Request Your Audit</h2>
          <p className="text-slate-400 text-sm mb-8">Secure your spot. 48-hour turnaround guaranteed.</p>
          
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); alert("Demo request submitted. (Simulated)"); }}>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Work Email</label>
              <input type="email" required className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-peridot-500 transition-colors" placeholder="ciso@company.com" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">First Name</label>
                <input type="text" required className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-peridot-500 transition-colors" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Last Name</label>
                <input type="text" required className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-peridot-500 transition-colors" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Company Size</label>
              <select className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-peridot-500 transition-colors appearance-none">
                <option>1,000 - 5,000 employees</option>
                <option>5,000 - 10,000 employees</option>
                <option>10,000+ employees</option>
              </select>
            </div>
            <button type="submit" className="w-full bg-peridot-500 hover:bg-peridot-400 text-slate-950 font-bold py-4 rounded-lg mt-4 transition-colors">
              Schedule Kickoff Call
            </button>
            <p className="text-xs text-slate-500 text-center mt-4">
              By submitting, you agree to our privacy policy regarding enterprise data handling.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}